import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import _ from 'lodash';
import { isHierarchySearchOutput } from '@/itemFinder/itemFinder.utilities';
import { getMaterializedTable } from '@/tableDefinitionEditor/materializedTable.utilities';
import { processMaterializedTable } from '@/tableDefinitionEditor/processedMaterializedTable.utilities';
import { getColumnsNotIncludedInAgGrid, isItemTableCell } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import { TableTypeEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import { FilterTypeEnum } from '@seeqdev/api/model/ValueFilter';
import type { ItemTableCell, ProcessedTableCell, TableJoinData } from '@/tableDefinitionEditor/tableDefinition.types';
import type { ItemFinderOutputV1 } from '@seeqdev/api/model/ItemFinderOutputV1';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';

export const buildTableJoinDataForAliasColumns = (itemFinder: ItemFinderOutputV1): TableJoinData[] =>
  itemFinder.searches
    .filter(isHierarchySearchOutput)
    .filter((search) => !!search.hierarchy.aliasColumnId)
    .map((search): TableJoinData => {
      const hierarchy = search.hierarchy;
      const tableDefinition = hierarchy.tableDefinitionOutput;
      const aliasColumnId = hierarchy.aliasColumnId!;
      const aliasColumn = tableDefinition.columnDefinitions.find((col) => col.id === aliasColumnId);

      return {
        tableJoinInput: { tableDefinitionId: tableDefinition.id, columnIdsToInclude: [aliasColumnId] },
        aliasColumnId,
        aliasColumnName: aliasColumn!.columnName,
      };
    });

/**
 * Fetches the whole row of cell values from a scaling table, only returning item cells.
 * @param tableDefinitionId
 * @param rowId - the id of the cell in the item id or csv item id column
 */
export const getItemsInScalingTableRow = async (
  tableDefinitionId: string,
  rowId: string,
): Promise<{ materializedTableRow: ItemTableCell[]; scalingTableData: TableDefinitionOutputV1 }> => {
  const { data: scalingTableData } = await sqTableDefinitionsApi.getTableDefinition({ id: tableDefinitionId });
  const scalingTableIsCsv = scalingTableData.tableType === TableTypeEnum.SCALINGCSV;
  const columnsNotIncludedInAgGrid = getColumnsNotIncludedInAgGrid(
    scalingTableData.tableType,
    scalingTableData.columnDefinitions,
  );
  const uuidColumns = scalingTableData.columnDefinitions
    .filter((column) => column.columnType === ColumnTypeEnum.UUID)
    .filter((column) => !columnsNotIncludedInAgGrid.includes(column.columnName));

  const navigationColumn = scalingTableData.navigationColumnId
    ? scalingTableData.columnDefinitions.find((col) => col.id === scalingTableData.navigationColumnId)
    : undefined;

  const columnNames = [
    ...uuidColumns.map((col) => col.columnName),
    ...(navigationColumn && !uuidColumns.some((col) => col.columnName === navigationColumn.columnName)
      ? [navigationColumn.columnName]
      : []),
  ];

  const propertyRequests: { uuidColumn: string; propertyNames: string[] }[] = [
    ...uuidColumns.map((col) => ({
      uuidColumn: col.columnName,
      propertyNames: [
        SeeqNames.Properties.Name,
        SeeqNames.Properties.Type,
        SeeqNames.Properties.CreatedByModelInstance,
      ],
    })),
  ];

  if (navigationColumn && navigationColumn.columnType === ColumnTypeEnum.UUID) {
    const navIndex = propertyRequests.findIndex((req) => req.uuidColumn === navigationColumn.columnName);
    const displayProperty = navigationColumn.displayProperty || SeeqNames.Properties.Name;
    if (navIndex !== -1) {
      propertyRequests[navIndex].propertyNames = _.uniq([...propertyRequests[navIndex].propertyNames, displayProperty]);
    } else {
      propertyRequests.push({
        uuidColumn: navigationColumn.columnName,
        propertyNames: _.uniq([
          SeeqNames.Properties.Name,
          SeeqNames.Properties.Type,
          SeeqNames.Properties.CreatedByModelInstance,
          displayProperty,
        ]),
      });
    }
  }

  const tableJoinData = scalingTableData.itemFinder
    ? buildTableJoinDataForAliasColumns(scalingTableData.itemFinder)
    : [];

  const tableData = await getMaterializedTable(
    tableDefinitionId,
    columnNames,
    propertyRequests,
    undefined,
    undefined,
    undefined,
    {
      valueFilter: {
        filterType: FilterTypeEnum.EQUALS,
        columnName: scalingTableIsCsv
          ? SeeqNames.MaterializedTables.CsvItemIdColumn
          : SeeqNames.MaterializedTables.ItemIdColumn,
        value: { value: rowId },
      },
    },
    tableJoinData.length > 0 ? tableJoinData.map((data) => data.tableJoinInput) : undefined,
  );

  const processedMaterializedTable = processMaterializedTable(
    tableData,
    scalingTableData.columnDefinitions.map((column) => {
      return {
        ...column,
        displayName: column.columnName,
        displayProperty: column.displayProperty,
        additionalProperties: [SeeqNames.Properties.CreatedByModelInstance],
      };
    }),
  );

  return {
    materializedTableRow: (processedMaterializedTable?.rows[0] ?? [])
      .map((row, index) => {
        return {
          ...row,
          columnName: processedMaterializedTable?.headers[index].name,
        };
      })
      .filter((cell: ProcessedTableCell) => isItemTableCell(cell)) as ItemTableCell[],
    scalingTableData,
  };
};
